/**
 * GSAP Button Hover Effects — Horizon Pro
 * 5 interactive hover effects selectable from theme settings.
 * Reads data-hover-effect from <body> to determine active effect.
 * Respects prefers-reduced-motion.
 */

(function () {
  'use strict';

  const SELECTOR = '.button, .button-secondary';
  const EFFECT = document.body.dataset.hoverEffect;

  if (!EFFECT || EFFECT === 'none') return;
  if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) return;

  function waitForGSAP(cb) {
    if (window.gsap) return cb();
    const check = setInterval(() => {
      if (window.gsap) {
        clearInterval(check);
        cb();
      }
    }, 50);
    // Safety: stop checking after 5s
    setTimeout(() => clearInterval(check), 5000);
  }

  /* ─── 1. Magnetic Pull ──────────────────────────────────── */
  function initMagnetic(btn) {
    const strength = 0.35;
    const onMove = (e) => {
      const rect = btn.getBoundingClientRect();
      const x = e.clientX - rect.left - rect.width / 2;
      const y = e.clientY - rect.top - rect.height / 2;
      gsap.to(btn, {
        x: x * strength,
        y: y * strength,
        duration: 0.3,
        ease: 'power2.out',
      });
    };
    const onLeave = () => {
      gsap.to(btn, {
        x: 0,
        y: 0,
        duration: 0.6,
        ease: 'elastic.out(1, 0.3)',
      });
    };
    btn.addEventListener('mousemove', onMove);
    btn.addEventListener('mouseleave', onLeave);
    btn._hpCleanup = () => {
      btn.removeEventListener('mousemove', onMove);
      btn.removeEventListener('mouseleave', onLeave);
    };
  }

  /* ─── 2. Liquid Fill ────────────────────────────────────── */
  function initLiquidFill(btn) {
    btn.style.overflow = 'hidden';
    btn.style.position = 'relative';
    btn.style.zIndex = '1';

    const fill = document.createElement('span');
    fill.className = 'hp-liquid-fill';
    Object.assign(fill.style, {
      position: 'absolute',
      top: '0',
      left: '0',
      width: '0',
      height: '0',
      borderRadius: '50%',
      background: 'var(--color-primary-button-hover-background, currentColor)',
      opacity: '0.15',
      transform: 'translate(-50%, -50%)',
      pointerEvents: 'none',
      zIndex: '-1',
    });
    btn.appendChild(fill);

    const onEnter = (e) => {
      const rect = btn.getBoundingClientRect();
      const x = e.clientX - rect.left;
      const y = e.clientY - rect.top;
      const size = Math.max(rect.width, rect.height) * 2.5;

      fill.style.left = x + 'px';
      fill.style.top = y + 'px';

      gsap.killTweensOf(fill);
      gsap.fromTo(
        fill,
        { width: 0, height: 0, opacity: 0.15 },
        { width: size, height: size, opacity: 0.2, duration: 0.5, ease: 'power2.out' }
      );
    };
    const onLeave = () => {
      gsap.to(fill, { width: 0, height: 0, opacity: 0, duration: 0.35, ease: 'power2.in' });
    };
    btn.addEventListener('mouseenter', onEnter);
    btn.addEventListener('mouseleave', onLeave);
    btn._hpCleanup = () => {
      btn.removeEventListener('mouseenter', onEnter);
      btn.removeEventListener('mouseleave', onLeave);
      fill.remove();
    };
  }

  /* ─── 3. Elastic Bounce ─────────────────────────────────── */
  function initElastic(btn) {
    const onEnter = () => {
      gsap.killTweensOf(btn);
      gsap.fromTo(
        btn,
        { scale: 1 },
        { scale: 1.06, duration: 0.5, ease: 'elastic.out(1, 0.3)' }
      );
    };
    const onLeave = () => {
      gsap.to(btn, { scale: 1, duration: 0.4, ease: 'elastic.out(1, 0.4)' });
    };
    btn.addEventListener('mouseenter', onEnter);
    btn.addEventListener('mouseleave', onLeave);
    btn._hpCleanup = () => {
      btn.removeEventListener('mouseenter', onEnter);
      btn.removeEventListener('mouseleave', onLeave);
    };
  }

  /* ─── 4. Sheen Sweep ────────────────────────────────────── */
  function initSheen(btn) {
    btn.style.overflow = 'hidden';
    btn.style.position = 'relative';

    const sheen = document.createElement('span');
    sheen.className = 'hp-sheen';
    Object.assign(sheen.style, {
      position: 'absolute',
      top: '0',
      left: '-100%',
      width: '100%',
      height: '100%',
      background: 'linear-gradient(120deg, transparent 30%, rgba(255,255,255,0.25) 50%, transparent 70%)',
      pointerEvents: 'none',
      zIndex: '1',
    });
    btn.appendChild(sheen);

    const onEnter = () => {
      gsap.killTweensOf(sheen);
      gsap.fromTo(
        sheen,
        { left: '-100%' },
        { left: '100%', duration: 0.6, ease: 'power2.inOut' }
      );
    };
    btn.addEventListener('mouseenter', onEnter);
    btn._hpCleanup = () => {
      btn.removeEventListener('mouseenter', onEnter);
      sheen.remove();
    };
  }

  /* ─── 5. Text Scramble ──────────────────────────────────── */
  function initScramble(btn) {
    const original = btn.textContent.trim();
    if (!original) return;
    const chars = 'ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyz0123456789!@#$%&*';
    let tween = null;

    const onEnter = () => {
      if (tween) tween.kill();
      const progress = { val: 0 };
      tween = gsap.to(progress, {
        val: 1,
        duration: 0.4,
        ease: 'none',
        onUpdate: () => {
          const resolved = Math.floor(progress.val * original.length);
          let result = '';
          for (let i = 0; i < original.length; i++) {
            if (original[i] === ' ') {
              result += ' ';
            } else if (i < resolved) {
              result += original[i];
            } else {
              result += chars[Math.floor(Math.random() * chars.length)];
            }
          }
          btn.textContent = result;
        },
        onComplete: () => {
          btn.textContent = original;
        },
      });
    };
    const onLeave = () => {
      if (tween) tween.kill();
      btn.textContent = original;
    };
    btn.addEventListener('mouseenter', onEnter);
    btn.addEventListener('mouseleave', onLeave);
    btn._hpCleanup = () => {
      btn.removeEventListener('mouseenter', onEnter);
      btn.removeEventListener('mouseleave', onLeave);
      btn.textContent = original;
    };
  }

  /* ─── Init & Observer ───────────────────────────────────── */
  const effects = {
    magnetic: initMagnetic,
    'liquid-fill': initLiquidFill,
    elastic: initElastic,
    sheen: initSheen,
    scramble: initScramble,
  };

  const initFn = effects[EFFECT];
  if (!initFn) return;

  const processed = new WeakSet();

  function applyEffect(root) {
    root.querySelectorAll(SELECTOR).forEach((btn) => {
      if (processed.has(btn)) return;
      processed.add(btn);
      initFn(btn);
    });
  }

  waitForGSAP(() => {
    applyEffect(document);

    // Watch for dynamically added buttons (section re-renders, drawers, etc.)
    const observer = new MutationObserver((mutations) => {
      for (const m of mutations) {
        for (const node of m.addedNodes) {
          if (node.nodeType !== 1) continue;
          if (node.matches && node.matches(SELECTOR) && !processed.has(node)) {
            processed.add(node);
            initFn(node);
          }
          if (node.querySelectorAll) {
            applyEffect(node);
          }
        }
      }
    });
    observer.observe(document.body, { childList: true, subtree: true });
  });
})();
